/**
 * Shared cart AJAX utilities
 * Used by both cart page and cart drawer
 */

window.CartAjax = {
  isUpdating: false,

  /** Set from cart.js on each render (ISO 4217, e.g. USD) */
  activeCurrency: 'USD',

  /**
   * Ajax cart + product JSON mix integer cents (e.g. 1000) with decimal dollar strings (e.g. "10.00").
   * Treat values with a decimal point as dollars; otherwise as cents.
   */
  normalizeMoneyToCents(value) {
    if (value == null || value === '') return 0
    const s = String(value).trim()
    if (/\d/.test(s) && s.includes('.')) {
      const n = parseFloat(s)
      return Number.isNaN(n) ? 0 : Math.round(n * 100)
    }
    const n = parseInt(s, 10)
    return Number.isNaN(n) ? 0 : n
  },

  formatMoney(amount) {
    const cents = CartAjax.normalizeMoneyToCents(amount)
    if (
      typeof Shopify !== 'undefined' &&
      typeof Shopify.formatMoney === 'function' &&
      window.theme &&
      window.theme.moneyFormat
    ) {
      return Shopify.formatMoney(cents, window.theme.moneyFormat)
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: CartAjax.activeCurrency || 'USD'
      }).format(cents / 100)
    } catch {
      return '$' + (cents / 100).toFixed(2)
    }
  },

  appendImageWidth(url) {
    if (!url) return ''
    let raw = String(url).trim()
    if (raw.startsWith('//')) raw = 'https:' + raw
    if (/[?&]width=\d/.test(raw)) return raw
    const join = raw.includes('?') ? '&' : '?'
    return `${raw}${join}width=300`
  },

  /** products.json: featured_image, image, or images[0] — string URL or { url, src } */
  getProductFeaturedImageUrl(product) {
    if (!product) return ''
    let f = product.featured_image || product.image
    if (!f && product.images && product.images[0]) f = product.images[0]
    if (!f) return ''
    const raw = typeof f === 'string' ? f : f.src || f.url || ''
    return CartAjax.appendImageWidth(raw)
  },

  /**
   * Mirrors snippets/cart-item.liquid discount logic for /cart.js line items.
   * @returns {{ percent: number, compareAtCents: number } | null}
   */
  getLineItemDiscount(item) {
    const orig = item.original_line_price
    const final = item.final_line_price
    if (orig > final) {
      return {
        percent: Math.round(((orig - final) / orig) * 100),
        compareAtCents: orig
      }
    }
    const spa = item.selling_plan_allocation
    if (spa && spa.compare_at_price > spa.price) {
      return {
        percent: Math.round(((spa.compare_at_price - spa.price) / spa.compare_at_price) * 100),
        compareAtCents: spa.compare_at_price
      }
    }
    return null
  },

  /**
   * Image URL for a /cart.js line item. Prefer `image`; fall back to `featured_image`.
   * Appends Shopify CDN `width=300` (protocol-relative URLs normalized to https).
   */
  getLineItemImageUrl(item) {
    let raw = ''
    if (typeof item.image === 'string' && item.image) {
      raw = item.image
    } else if (item.image && typeof item.image === 'object') {
      raw = item.image.src || item.image.url || ''
    }
    if (!raw && item.featured_image) {
      const f = item.featured_image
      raw = typeof f === 'string' ? f : f.url || f.src || ''
    }
    return CartAjax.appendImageWidth(raw)
  },

  async fetchCart() {
    const response = await fetch('/cart.js')
    return response.json()
  },

  async updateQuantity(id, quantity) {
    if (this.isUpdating) return null
    this.isUpdating = true

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id, quantity })
      })
      const cart = await response.json()
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }))
      return cart
    } catch (error) {
      console.error('Failed to update cart:', error)
      return null
    } finally {
      this.isUpdating = false
    }
  },

  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      })
      if (response.ok) {
        document.dispatchEvent(new CustomEvent('cart:updated'))
        return await response.json()
      }
      return null
    } catch (error) {
      console.error('Failed to add to cart:', error)
      return null
    }
  },

  /**
   * Locale-aware URLs (Shopify.routes.root). Related recommendations often return [] with an
   * empty cart and no product_id; collection products are reliable for empty-cart UIs.
   */
  shopifyRootUrl(path) {
    const root =
      typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/'
    const base = root.endsWith('/') ? root : root + '/'
    const p = path.startsWith('/') ? path.slice(1) : path
    return base + p
  },

  async fetchRecommendations(limit = 4) {
    const fetchProducts = async (url) => {
      try {
        const r = await fetch(url)
        if (!r.ok) return []
        const data = await r.json()
        return data.products && data.products.length > 0 ? data.products : []
      } catch (e) {
        console.error('fetchRecommendations failed:', url, e)
        return []
      }
    }

    const q = `limit=${limit}`
    const collectionFirst = await fetchProducts(
      `${this.shopifyRootUrl('collections/all/products.json')}?${q}`
    )
    if (collectionFirst.length > 0) return collectionFirst

    const related = await fetchProducts(
      `${this.shopifyRootUrl('recommendations/products.json')}?${q}&intent=related`
    )
    if (related.length > 0) return related

    return fetchProducts(`${this.shopifyRootUrl('recommendations/products.json')}?${q}&intent=complementary`)
  },

  updateShippingMessage(messageEl, cartTotal, threshold) {
    if (!messageEl) return

    const remaining = threshold - cartTotal
    if (cartTotal === 0) {
      messageEl.innerHTML = `Get <strong>FREE shipping</strong> with a ${this.formatMoney(threshold)} purchase!`
    } else if (remaining > 0) {
      messageEl.innerHTML = `Add ${this.formatMoney(remaining)} only and get <strong>FREE shipping!</strong>`
    } else {
      messageEl.innerHTML = `You've unlocked <strong>FREE shipping!</strong>`
    }
  },

  updateHeaderCount(count) {
    document.querySelectorAll('.header-cart-count').forEach(el => {
      el.textContent = count
      el.classList.toggle('hidden', count === 0)
    })
  },

  updateCheckoutButton(btn, isEmpty) {
    if (!btn) return
    btn.classList.toggle('pointer-events-none', isEmpty)
    btn.classList.toggle('opacity-50', isEmpty)
  }
}

if (typeof window.CartAjaxInitialCurrency === 'string' && window.CartAjaxInitialCurrency) {
  window.CartAjax.activeCurrency = window.CartAjaxInitialCurrency
}
